import React from "react";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";

// Utility: safely split plain text by accent parts
function highlightAccentParts(text, accentParts, accentColor, headerStyle) {
    let lastIndex = 0;
    let parts = [];
    accentParts.forEach((part, i) => {
        const index = text.indexOf(part, lastIndex);
        if (index !== -1) {
            if (index > lastIndex) {
                parts.push(
                    <span key={`normal-${lastIndex}`} className={`${headerStyle}`}>{text.substring(lastIndex, index)}</span>
                );
            }
            parts.push(
                <span
                    key={`accent-${index}-${i}`}
                    className={`${headerStyle} accent-color-text`}
                    style={{ color: accentColor }}
                >
                    {part}
                </span>
            );
            lastIndex = index + part.length;
        }
    });
    if (lastIndex < text.length) {
        parts.push(<span key={`normal-end`} className={`${headerStyle}`}>{text.substring(lastIndex)}</span>);
    }
    return parts;
}

// Utility: handle HTML string, preserving tags and only accenting text nodes
function highlightAccentPartsInHtml(html, accentParts, accentColor, headerStyle) {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(`<span>${html}</span>`, "text/html");
    const root = doc.body.firstChild;

    // List of void elements that cannot have children
    const VOID_ELEMENTS = [
        "area", "base", "br", "col", "embed", "hr", "img", "input",
        "link", "meta", "param", "source", "track", "wbr"
    ];

    function walk(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            return highlightAccentParts(node.nodeValue, accentParts, accentColor, headerStyle);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const Tag = node.tagName.toLowerCase();
            // Handle void elements (e.g. <br/>) without children
            if (VOID_ELEMENTS.includes(Tag)) {
                return [React.createElement(Tag, { key: Math.random() })];
            }
            const children = [];
            node.childNodes.forEach((child) => {
                children.push(...walk(child));
            });
            return [React.createElement(Tag, { key: Math.random() }, children)];
        }
        return [];
    }

    // Remove the outer <span> wrapper
    const children = [];
    root.childNodes.forEach((child) => {
        children.push(...walk(child));
    });
    return children;
}

const useColoredTextParser = ({
    title,
    accentTitleParts,
    accentColor = "#f26823",
    headerStyle = "h2",
    seoStyle = "",
    classes = "",
    HeadingsComponent = null,
}) => {
    const renderFormattedTitle = React.useCallback(() => {
        if (!title) return null;

        // Parse accent parts (JSON array or comma-separated)
        let accentParts = [];
        if (accentTitleParts) {
            try {
                accentParts = JSON.parse(accentTitleParts);
                if (!Array.isArray(accentParts)) throw new Error();
            } catch {
                accentParts = accentTitleParts.split(",").map((p) => p.trim()).filter(Boolean);
            }
        }

        if (accentParts.length > 0) {
            const isHtml = /<\/?[a-z][\s\S]*>/i.test(title);
            let parts;
            if (isHtml && typeof window !== "undefined" && window.DOMParser) {
                try {
                    parts = highlightAccentPartsInHtml(title, accentParts, accentColor, headerStyle);
                } catch (e) {
                    parts = highlightAccentParts(title.replace(/<[^>]+>/g, ""), accentParts, accentColor, headerStyle);
                }
            } else {
                parts = highlightAccentParts(title, accentParts, accentColor, headerStyle);
            }
            return <Paragraph type={headerStyle} text={parts} classes={`${classes} ${headerStyle}`}  seo_type={seoStyle}/>;
        }

        if (HeadingsComponent) {
            return <HeadingsComponent text={title} type={seoStyle || headerStyle}  classes={`${headerStyle}`}/>;
        }
        return <Paragraph type={headerStyle} text={title} classes={`${classes} ${headerStyle}`} seo_type={seoStyle}/>;
    }, [title, accentTitleParts, accentColor, headerStyle, HeadingsComponent, classes]);
   
    return renderFormattedTitle;
};

export default useColoredTextParser;
